import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="cart-drawer"
//
// A right-side native <dialog> that lazy-loads the full cart (GET /carrinho?drawer=1)
// into its Turbo Frame. Opened by the nav CARRINHO buttons (elements carrying
// data-cart-drawer-trigger, anywhere on the page) and by the add-to-cart
// turbo_stream (the "cart-drawer:open" window event). Mirrors the native-dialog
// open/close of side_navigation_drawer and the lazy-src reset of drawer_frame.
export default class extends Controller {
	static targets = ["frame"];
	static values = { url: String };

	// The nav buttons live outside the dialog, so we listen on the window and
	// filter to a trigger element. On the cart page itself we let the link
	// navigate normally instead of shadowing it with the drawer.
	openFromNav(event) {
		const trigger = event.target.closest("[data-cart-drawer-trigger]");
		if (!trigger) return;

		const href = trigger.getAttribute("href");
		if (href && new URL(href, window.location.origin).pathname === window.location.pathname) return;

		event.preventDefault();
		this.load();
		this.show();
	}

	// The add-to-cart stream already refreshed the frame content, so just reveal it.
	open() {
		if (this.frameTarget.getAttribute("src") === null && !this.frameTarget.hasChildNodes()) this.load();
		this.show();
	}

	load() {
		if (this.frameTarget.getAttribute("src") !== this.urlValue) {
			this.frameTarget.setAttribute("src", this.urlValue);
		}
	}

	show() {
		if (this.element.open) return;

		// Close any other open dialog first (e.g. the mobile menu) so we never
		// stack two modals.
		document.querySelectorAll("dialog[open]").forEach((dialog) => {
			if (dialog !== this.element) dialog.close();
		});

		if (typeof this.element.showModal === "function") {
			this.element.showModal();
		} else {
			this.element.setAttribute("open", "");
		}
	}

	closeDialog() {
		if (typeof this.element.close === "function") {
			this.element.close();
		} else {
			this.element.removeAttribute("open");
			this.reset();
		}
	}

	closeOnBackdrop(event) {
		if (event.target === this.element) this.closeDialog();
	}

	// Esc closes natively; kept explicit for parity with the other drawers.
	closeWithKeyboard(event) {
		if (event.code === "Escape" && this.element.open) this.closeDialog();
	}

	// Navigating away (or caching the page) must not leave the drawer open.
	closeIfNavigated() {
		if (this.element.open) this.closeDialog();
	}

	// drawer_frame idiom: drop the src so the next open re-fetches a fresh cart.
	reset() {
		this.frameTarget.removeAttribute("src");
		this.frameTarget.removeAttribute("complete");
		this.frameTarget.innerHTML = "";
	}
}
